/**
 * Draft projections shared by the Drafts page and the home page.
 *
 * A draft's pool is the players whose contracts expire that year (a player with
 * `contract: 1` expires before the next season's draft, `2` the year after, ...).
 * Before each draft every manager may franchise tag one of their expiring
 * players, and a player who was tagged last time cannot be tagged again.
 *
 * Estimate: each manager tags their highest-ranked (by Yahoo rank) tag-eligible
 * expiring player. Those players leave the pool, and the best remaining players
 * by rank are projected to go in pick order.
 */

import { rookiePlaceholdersFor } from './rookieIntake.js';

// Every draft brings in a few rookies, usually two or three in round 1. We don't know
// who, so round 1 shows placeholders at fixed picks (see rookieIntake.js to edit them);
// the best remaining vets fill the other picks.

// Lower Yahoo rank is better; players with no rank sort last.
const rankOf = player => {
    const rank = Number(player.yahooleaguerank);
    return Number.isFinite(rank) && rank > 0 ? rank : Infinity;
};

export const byRank = (a, b) => rankOf(a) - rankOf(b) || String(a.name).localeCompare(String(b.name));

/** Players whose contracts expire in `year`, best Yahoo rank first. */
export function getDraftPool(playerData, year, currentSeason) {
    const yearsAway = year - currentSeason;
    return playerData.filter(player => player.contract === yearsAway).sort(byRank);
}

/** One player per manager: their best-ranked expiring player who has not been tagged before. */
export function estimateFranchiseTags(pool) {
    const best = new Map();
    pool.forEach(player => {
        if (!player.manager || player.franchisetag) {
            return;
        }
        const current = best.get(player.manager);
        if (!current || byRank(player, current) < 0) {
            best.set(player.manager, player);
        }
    });
    return [...best.entries()]
        .map(([manager, player]) => ({ manager, player }))
        .sort((a, b) => byRank(a.player, b.player));
}

/**
 * Project a draft: the pool, the estimated franchise tags, and the players
 * left to draft (best rank first) with who would take each early pick.
 *
 * `order` is the spot-by-spot manager order for round 1 and `outgoingPicks` the
 * traded-pick map, so a traded pick shows its new owner.
 */
export function projectDraft({
    playerData,
    year,
    currentSeason,
    order = [],
    outgoingPicks = {},
    rookies = rookiePlaceholdersFor(year),
}) {
    const pool = getDraftPool(playerData, year, currentSeason);
    const tags = estimateFranchiseTags(pool);
    const taggedIds = new Set(tags.map(t => t.player.id));
    const available = pool.filter(p => !taggedIds.has(p.id));

    const rookieAtPick = new Map(
        rookies.map(({ pick, position, label }) => [
            pick,
            { id: null, name: label, posname: position, isRookie: true },
        ])
    );

    // Rookies take their fixed slots; the best remaining vets fill the other picks in rank order.
    let nextVet = 0;
    const picks = order.map((manager, i) => {
        const pick = i + 1;
        const tradedTo = outgoingPicks[manager] && outgoingPicks[manager][year]
            ? outgoingPicks[manager][year][1] || null
            : null;
        const player = rookieAtPick.get(pick) || available[nextVet++] || null;
        return { pick, manager, tradedTo, player };
    });

    return { pool, tags, taggedIds, available, picks };
}
